import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { setDataLayer } from '@/hooks/use-gtm';

let tries = 0;
const maxTries = 50;
let timeout: null | NodeJS.Timeout = null;

export const HIDE_CHATBOT_CLASSNAME = 'hide-chatbot';
export const TELIA_SCRIPT_ID = 'ace-telia-sdk';
const CLASSNAME = 'humany-widget-inactive';
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function activateWidget(widgetName: string, open = false) {
    if (tries === maxTries) {
        return;
    }
    tries++;

    try {
        const widget = window?.humany?.widgets?.find(widgetName);
        if (!widget) {
            timeout = setTimeout(() => activateWidget(widgetName, open), 250);
            return;
        }

        document.body.classList.remove(CLASSNAME);
        if (open) {
            widget.invoke('open');
        }

        tries = 0;
    } catch (error) {
        console.error(error);
    }
}

export async function deactivateWidget(widgetName: string) {
    try {
        const widget = window?.humany?.widgets?.find(widgetName);
        if (widget && widget?.state !== 'deactivated') {
            document.body.classList.add(CLASSNAME);
        }
    } catch (error) {
        console.error(error);
    }
}

export async function addScriptTag({
    widgetName,
    delay,
    activate = true,
    open = false,
}: {
    widgetName: string;
    delay?: number | null;
    activate?: boolean;
    open?: boolean;
}) {
    if (document.getElementById(TELIA_SCRIPT_ID)) {
        if (activate) {
            activateWidget(widgetName, open);
        }
        return;
    }

    if (delay) {
        await sleep(delay);
    }

    const script = document.createElement('script');
    script.id = TELIA_SCRIPT_ID;
    script.src = '//wds.ace.teliacompany.com/wds/instances/JpnKjqJs/ACEWebSDK.min.js';
    script.async = true;

    script.onload = () => {
        if (activate) {
            activateWidget(widgetName, open);
        }
    };

    document.body.appendChild(script);
}

export const HumanyChatBot = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { pageQuery, getSetting } = useGlobalQueries();
    const zendeskSettings = getSetting('zendesk_settings');
    const botInterface = getSetting('humany_settings')?.humany_ai_chatbot_widget_name || 'resurs-bot-v5';
    const activeGlobally = true; //getSetting('humany_settings')?.humany_global_chatbot_enable || false;
    const chatbotText = getSetting('humany_settings')?.humany_global_chatbot_text || '';
    const widgetName = botInterface.toLocaleLowerCase().replace(/\s+/g, '-');

    const gtmChatbotCallback = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target?.matches('div.humany-widget button')) {
            setDataLayer({
                action: 'click',
                category: 'chatbot',
                label: target?.innerText,
            });
        }
    };

    const init = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsLoading(true);
        addScriptTag({ widgetName, open: true });
    };

    useEffect(() => {
        if (zendeskSettings?.zendesk_chatbot_activate) {
            return;
        }

        if (activeGlobally) {
            if (pageQuery?.data?.hide_ai_chatbot) {
                document.body.classList.add(HIDE_CHATBOT_CLASSNAME);
            } else {
                document.body.classList.remove(HIDE_CHATBOT_CLASSNAME);
            }
            return;
        }

        if (pageQuery?.data?.show_ai_chatbot) {
            document.body.classList.remove(HIDE_CHATBOT_CLASSNAME);
        } else {
            document.body.classList.add(HIDE_CHATBOT_CLASSNAME);
        }
    }, [pageQuery, activeGlobally, zendeskSettings]);

    useEffect(() => {
        if (zendeskSettings?.zendesk_chatbot_activate) {
            return;
        }

        document.body.addEventListener('click', gtmChatbotCallback);
        return () => {
            document.body.removeEventListener('click', gtmChatbotCallback);

            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, []);

    if (zendeskSettings?.zendesk_chatbot_activate) {
        return null;
    }

    return (
        <>
            <a href={`//resurs-bank.humany.net/${widgetName}`} role="button" />
            {isLoading ? (
                <div className="humany-loading-indicator r-fixed r-bottom-6 r-right-6 r-flex r-h-14 r-w-14 r-content-center r-items-center r-justify-center sm:r-h-[72px] sm:r-w-[72px]">
                    <svg
                        aria-hidden="true"
                        className="r-h-10 r-w-10 r-animate-spin r-fill-secondary-500 r-text-white"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"
                        />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"
                        />
                    </svg>
                </div>
            ) : (
                <button
                    onClick={init}
                    className={clsx('rtf-humany-chatbot r-fixed r-bottom-6 r-right-6 r-z-40 r-flex r-h-16 r-items-center', {
                        'md:r-gap-4 md:r-rounded-l-3xl md:r-rounded-r-[99px] md:r-bg-white md:r-pl-4 md:r-shadow-md': chatbotText,
                    })}
                >
                    {chatbotText && <span className="r-font-bold r-text-primary max-md:r-hidden">{chatbotText}</span>}
                    <span className="r-flex r-h-14 r-w-14 r-transform r-items-center r-justify-center r-rounded-full r-bg-secondary-500 r-transition-transform r-duration-300 hover:r-scale-110 sm:r-h-16 sm:r-w-16">
                        <img className="max-sm:r-w-8 sm:r-w-9" src="https://www.resursbank.se/assets/chat-e1c8b333.svg" alt="chat-icon" />
                    </span>
                </button>
            )}
        </>
    );
};
